// Estado do modal de depósito da tela de jogo (ENG-361). Independente do
// fluxo de depósito da área principal (useLayoutStore().modalType === 'deposit'):
// tem seu próprio estado de abertura, etapa atual e resposta do PIX, pra que o
// polling e a UI não dependam do modal global.
//
// Singleton em nível de módulo (mesmo padrão de useGameMobilePreview): os refs
// vivem fora da função, então todos os consumidores compartilham o estado.

export type GameDepositStep = 1 | 2 | 3;

// Dados da conta bancária que pagou, montados quando o depósito é aprovado.
// É o que fecha o ciclo do consentimento `save_bank_account`: o jogador
// autorizou cadastrar a conta, então precisa ver QUAL conta foi.
export type GameDepositApprovedInfo = {
  amountLabel: string;
  bankAccountLabel: string | null;
  agencyAccountLabel: string | null;
  differentAccount: boolean;
  isNewAccount: boolean;
};

const isOpen = ref(false);
const step = ref<GameDepositStep>(1);

// Recusa (status `error` do /wallet/charge): titularidade divergente — a conta
// que pagou é de outro CPF. Estado à parte das etapas porque não é um passo do
// fluxo, é uma saída dele.
const isDenied = ref(false);

const approvedInfo = ref<GameDepositApprovedInfo | null>(null);

// Resposta do POST /wallet/add-credit, consumida pela Etapa 2 (PIX).
const paymentRes = ref<DefaultDepositInfoResponse>({
  qr_code: '',
  br_code: '',
  clabe: '',
  payment_link: '',
  barcode: '',
  barcode_number: '',
  astropay_response: null,
  checkout_page: null,
  value: 0,
  amount: 0,
});

function resetPaymentRes() {
  paymentRes.value = {
    qr_code: '',
    br_code: '',
    clabe: '',
    payment_link: '',
    barcode: '',
    barcode_number: '',
    astropay_response: null,
    checkout_page: null,
    value: 0,
    amount: 0,
  };
}

export function useGameDepositModal() {
  function open() {
    step.value = 1;
    isDenied.value = false;
    approvedInfo.value = null;
    resetPaymentRes();
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
    // Volta ao estado inicial pra próxima abertura começar limpa na Etapa 1.
    step.value = 1;
    isDenied.value = false;
    approvedInfo.value = null;
    resetPaymentRes();
  }

  // Volta do resultado pro formulário sem fechar o modal (o "voltar" das telas
  // de pagamento do fluxo antigo, `backToPaymentScreen`).
  function backToForm() {
    isDenied.value = false;
    approvedInfo.value = null;
    step.value = 1;
    resetPaymentRes();
  }

  function goToStep(target: GameDepositStep) {
    step.value = target;
  }

  return {
    isOpen,
    step,
    paymentRes,
    isDenied,
    approvedInfo,
    open,
    close,
    backToForm,
    goToStep,
    resetPaymentRes,
  };
}
